import React from 'react';
import { Check, X } from 'lucide-react';
import { SubscriptionPlan } from '../types';
import { Button } from '../../../components/ui/Button';

interface PlanCardProps {
  plan: SubscriptionPlan;
  isSelected: boolean;
  onSelect: (planId: string) => void;
  isProcessing: boolean;
}

export const PlanCard: React.FC<PlanCardProps> = ({ 
  plan, 
  isSelected, 
  onSelect, 
  isProcessing 
}) => {
  const isPremium = plan.plan_type === 'premium';
  const isFree = plan.plan_type === 'free';

  return (
    <div
      className={`relative rounded-2xl border-2 p-8 transition-all ${
        isPremium
          ? 'border-blue-500 shadow-xl scale-105'
          : 'border-gray-200 hover:border-blue-300'
      } ${isSelected ? 'ring-4 ring-blue-200' : ''}`}
    >
      {isPremium && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
            Most Popular
          </span>
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.plan_name}</h3>
        <div className="flex items-baseline justify-center gap-2">
          <span className="text-4xl font-bold text-gray-900">
            {plan.price_vatu.toLocaleString()}
          </span>
          <span className="text-gray-600">VT</span>
          <span className="text-gray-500 text-sm">/month</span>
        </div>
      </div>

      <ul className="space-y-4 mb-8">
        {plan.features.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <span className="text-gray-700">{feature}</span>
          </li>
        ))}

        {isFree && (
          <>
            <li className="flex items-start gap-3">
              <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-500">Limited to {plan.max_group_members} group members</span>
            </li>
            <li className="flex items-start gap-3">
              <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-500">
                Maximum {plan.max_contributions_per_month} contributions/month
              </span>
            </li>
          </>
        )}
      </ul>

      <Button
        onClick={() => onSelect(plan.id)}
        disabled={isProcessing}
        className={`w-full py-3 rounded-xl font-semibold transition-all ${
          isPremium
            ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg'
            : isFree
            ? 'bg-gray-100 text-gray-900 hover:bg-gray-200' :'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        {isSelected ? 'Selected' : 'Select Plan'}
      </Button>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Reporting</p>
            <p className="font-semibold text-gray-900 capitalize">
              {plan.features.reporting}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Support</p>
            <p className="font-semibold text-gray-900 capitalize">
              {plan.features.support}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Storage</p>
            <p className="font-semibold text-gray-900">{plan.features.storage}</p>
          </div>
        </div>
      </div>
    </div>
  );
};